import React from 'react';
import {DataGrid, GridColDef, GridRowSelectionModel, GridValueGetterParams} from '@mui/x-data-grid';
import {TemplateWithoutBody} from '../../model/Template';
import classes from '../../styling/components/TemplateTable.module.css';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {SendTestmailButton} from "./SendTestmailButton";
import EditIcon from '@mui/icons-material/Edit';
import PageviewIcon from '@mui/icons-material/Pageview';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import IconButton from '@mui/material/IconButton';
import {useTemplateEmailPreviewDialog} from '../../hooks/useTemplateEmailPreviewDialog';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

interface ColumnVisibility {
    difficulty?: boolean;
    loginPage?: boolean;
    campaigns?: boolean;
    testmail?: boolean;
    edit?: boolean;
    preview?: boolean;
}

interface Props {
    templates: TemplateWithoutBody[],
    selectedTemplateIds: string[],
    onSelectionChange?: (newSelection: string[]) => void,
    readonly?: boolean,
    columnVisibility?: ColumnVisibility,
    pageSize?: number
}

const TemplateTable: React.FC<Props> = (props) => {

    const {templates, selectedTemplateIds, onSelectionChange, readonly, columnVisibility, pageSize = 5} = props;

    const {t} = useTranslation();
    const {customerId} = useParams<{ customerId: string }>();
    const [previewDialog, openPreviewDialog] = useTemplateEmailPreviewDialog();

    const rows = templates.slice().sort((a, b) => {
        const aSelected = selectedTemplateIds.includes(a.templateId!);
        const bSelected = selectedTemplateIds.includes(b.templateId!);
        return aSelected === bSelected ? 0 : aSelected ? -1 : 1;
    })

    const allExtendedColumns: Record<keyof ColumnVisibility, GridColDef> = {
        difficulty: {
            field: 'difficulty',
            headerName: `${t('templateTable.difficulty') || 'Difficulty'}`,
            flex: 0.8,
            minWidth: 110,
            sortComparator: (v1, v2) => {
                const difficultyOrder: Record<string, number> = {
                    easy: 1,
                    medium: 2,
                    hard: 3
                };
                const v1Lower = (v1 || '').toString().toLowerCase();
                const v2Lower = (v2 || '').toString().toLowerCase();
                const order1 = difficultyOrder[v1Lower] || 999;
                const order2 = difficultyOrder[v2Lower] || 999;
                return order1 - order2;
            },
            renderCell: (params) => {
                const value = (params.value || '').toString().toLowerCase();
                const colorMap: Record<string, string> = {
                    easy: '#2e7d32',      // green
                    medium: '#f9a825',    // yellow
                    hard: '#c62828'       // red
                };
                const labelMap: Record<string, string> = {
                    easy: t('templateTable.difficultyEasy') || 'Easy',
                    medium: t('templateTable.difficultyMedium') || 'Medium',
                    hard: t('templateTable.difficultyHard') || 'Hard',
                };
                const color = colorMap[value] || '#9e9e9e';
                const label = labelMap[value] || params.value || '—';

                switch (value) {
                    case 'easy':
                    case 'medium':
                    case 'hard':
                        return (
                            <span style={{display: 'flex', alignItems: 'center', gap: 6}}>
                                <FiberManualRecordIcon sx={{fontSize: 16, color}} />
                                {label}
                            </span>
                        );
                    default:
                        return params.value || '—';
                }
            }
        },
        loginPage: {
            field: 'hasLoginPage',
            headerName: `${t('templateTable.loginPage') || 'Loginpage'}`,
            flex: 0.7,
            minWidth: 90,
            valueGetter: (params: GridValueGetterParams) => params.row.hasLoginPage,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <div style={{display: 'flex', justifyContent: 'center', width: '100%'}}>
                    {params.value
                        ? <CheckCircleOutlineIcon fontSize="small" color="success" />
                        : <HighlightOffIcon fontSize="small" color="error" />}
                </div>
            )
        },
        campaigns: {
            field: 'campaignNames',
            headerName: `${t('templateTable.campaigns') || 'Campaigns'}`,
            flex: 1.4,
            minWidth: 145,
            valueGetter: (params: GridValueGetterParams) => {
                const names: string[] = params.row.campaignNames || [];
                return names.length > 0 ? names.join(', ') : '—';
            }
        },
        testmail: {
            field: 'testmail', 
            headerName: '', 
            sortable: false,
            width: 50,
            valueGetter: (params: GridValueGetterParams) => params.row.templateId,
            renderCell: (params) => (
                <SendTestmailButton templateId={params.value}/>
            )
        },
        edit: {
            field: 'edit', 
            headerName: '', 
            sortable: false,
            width: 50,
            valueGetter: (params: GridValueGetterParams) => params.row.templateId,
            renderCell: (params) => (
                <Link to={`${params.value}`}><EditIcon/></Link>
            )
        },
        preview: {
            field: 'preview', 
            headerName: '', 
            sortable: false,
            minWidth: 70,
            valueGetter: (params: GridValueGetterParams) => params.row.templateId,
            renderCell: (params) => (
                <IconButton
                    size="small"
                    onClick={(event) => {
                        event.stopPropagation();
                        if (customerId) {
                            openPreviewDialog(params.value, customerId);
                        }
                    }}
                >
                    <PageviewIcon color="look2xBlue" />
                </IconButton>
            )
        }
    };

    const extendedColumns: GridColDef[] = columnVisibility
        ? Object.entries(allExtendedColumns)
            .filter(([key]) => columnVisibility[key as keyof ColumnVisibility])
            .map(([, column]) => column)
        : [];

    const columns: GridColDef[] = [
        {field: 'name', headerName: `${t('templateTable.name')}`, flex: 1.4, minWidth: 150},
        {field: 'senderAddress', headerName: `${t('templateTable.senderAddress')}`, flex: 1.4, minWidth: 150},
        ...extendedColumns
    ];

    const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
        if (!readonly && !!onSelectionChange) {
            onSelectionChange(newSelection as string[]);
        }
    }

    return (
        <>
            {previewDialog}
            <div className={classes.templateTable}>
                <DataGrid
                    rows={rows}
                    getRowId={(row) => row.templateId}
                    columns={columns}
                    localeText={{
                        MuiTablePagination: {
                            labelRowsPerPage: t('dataGrid.pagination.labelRowsPerPage') || 'Rows per page:',
                            labelDisplayedRows: ({ from, to, count }) => 
                                t('dataGrid.pagination.labelDisplayedRows', { from, to, count }) || `${from}-${to} of ${count}`,
                        }
                    }}
                    disableColumnMenu={true}
                    disableRowSelectionOnClick={true}
                    initialState={{
                        pagination: {
                            paginationModel: {page: 0, pageSize: pageSize},
                        },
                    }}
                    pageSizeOptions={[]}
                    checkboxSelection={!readonly}
                    {...!readonly ? {
                        rowSelectionModel: selectedTemplateIds,
                        onRowSelectionModelChange: handleSelectionChange
                    } : {}}
                />
            </div>
        </>
    );
}

export default TemplateTable;